/**
 * /reserva-starlink-mini/saldo-ok — Post-Stripe success for balance payment.
 * Polls /api/starlink/reservations/balance-status/{session_id} until status
 * becomes `saldo_pagado`.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CheckCircle2, Loader2, Truck } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function StarlinkSaldoOkPage() {
  const { setLanguage } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => { setLanguage("es"); document.title = "Saldo pagado · Starlink Mini | GTX"; }, [setLanguage]);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let stop = false;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/starlink/reservations/balance-status/${sessionId}`);
        tries += 1;
        if (stop) return;
        if (r.data.status === "saldo_pagado" || r.data.status === "enviado") {
          setState({ status: "paid", data: r.data });
        } else if (tries >= 15) {
          setState({ status: "timeout", data: r.data });
        } else {
          setTimeout(poll, 2000);
        }
      } catch {
        if (!stop) setState({ status: "error", data: null });
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise flex items-center justify-center px-5 py-16" data-testid="starlink-saldo-ok-page">
      <Helmet><meta name="robots" content="noindex" /></Helmet>
      <div className="w-full max-w-lg">
        {state.status === "paid" && (
          <div className="rounded-2xl border border-emerald-500/40 bg-[#0F0F16] p-8 lg:p-10 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-300" />
            </div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-emerald-400 mono mb-2">Saldo pagado</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mb-3">¡Pago completo!</h1>
            <p className="text-white/70 mb-6">Hemos recibido el saldo de {fmtEUR(state.data?.importe_saldo)}. Preparamos tu Starlink Mini y sale en <b>24-48 horas</b>.</p>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left space-y-3 mb-6">
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50">Nº de pedido</span>
                <span className="font-black text-lg text-blue-300 mono tracking-wider">{state.data?.order_number}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60 pt-3 border-t border-white/8">
                <Truck className="w-4 h-4 text-emerald-300" />
                Envío en 24-48h · te avisaremos con el nº de seguimiento
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/mi-reserva" className="flex-1">
                <button className="w-full bg-blue-500 hover:bg-blue-400 text-white rounded-lg px-5 py-3 text-sm font-bold">Seguir mi reserva</button>
              </Link>
              <Link to="/" className="flex-1">
                <button className="w-full border border-white/15 hover:bg-white/5 text-white/80 rounded-lg px-5 py-3 text-sm font-semibold">Volver al inicio</button>
              </Link>
            </div>
          </div>
        )}

        {(state.status === "loading" || (state.status !== "paid" && state.status !== "timeout" && state.status !== "error")) && (
          <div className="rounded-2xl border border-white/10 bg-[#0F0F16] p-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
            <div className="font-bold mb-1">Confirmando tu pago…</div>
            <p className="text-white/50 text-sm">Un momento — estamos hablando con Stripe.</p>
          </div>
        )}

        {state.status === "timeout" && (
          <div className="rounded-2xl border border-yellow-500/40 bg-[#0F0F16] p-8 text-center">
            <div className="font-bold mb-2">Estamos verificando tu pago</div>
            <p className="text-white/60 text-sm mb-4">Stripe tarda un poco más de lo normal. En unos minutos verás el saldo como pagado en el seguimiento.</p>
            <Link to="/mi-reserva" className="text-blue-300 text-sm underline">Ver mi reserva</Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-2xl border border-red-500/40 bg-[#0F0F16] p-8 text-center">
            <div className="font-bold mb-2">No se ha podido verificar el pago</div>
            <p className="text-white/60 text-sm mb-4">Escríbenos y lo resolvemos rápido: <a className="text-blue-300 underline" href="mailto:adm@globaltechxperts.nl">adm@globaltechxperts.nl</a>.</p>
            <Link to="/mi-reserva" className="text-blue-300 text-sm underline">Ver mi reserva</Link>
          </div>
        )}
      </div>
    </div>
  );
}
